import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Card, Col, Row, Skeleton, Tabs} from "antd";
import {CreditCardOutlined} from "@ant-design/icons";

import ErrorBoundary from "../../../components/ErrorBoundary";
import BillingBalancesStaff from "./BillingBalancesStaff";
import TransactionHistory from "../../../components/billings/TransactionHistory";
import CreditCreateForm from "../../../components/billings/CreditCreateForm";
import Deposits from "../../../components/billings/Deposits";
import PaymentCreateForm from "../../../components/billings/PaymentCreateForm";
import ChargeHistory from "../../../components/billings/ChargeHistory";
import PageTitle from "../../../components/page/PageTitle";
import {getAccountMembershipNumber, getActiveAccount, getActiveProfile} from "../../../lib/activeMember";
import {getTaxes} from "../../../helpers/billing";
import {messageType, showMessages} from "../../../helpers/message";
import StoredCreditCardCardStaff from "./StoredCreditCardCardStaff";
import {isModuleAvailable, MODULE_BILLING_CREDIT_CARD_PAYMENT} from "../../../lib/site";
import {userHasRole} from "../../../lib/role";
import ChargeCreditCardFormStaff from "../../../components/billings/ChargeCreditCardFormStaff";
import PromotionalCredits from "../promotions/PromotionalCredits";

const BillingStaff = () => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const navigate = useNavigate();

    const [taxes, setTaxes] = useState([]);
    const [refreshBalances, setRefreshBalances] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refetchStoredCard, setRefetchStoredCard] = useState(false);
    const [accountingBalance, setAccountingBalance] = useState(0);

    const isMCT = userHasRole('staff', 'developer') || userHasRole('staff', 'member-care');
    const isMarketing = userHasRole('staff', 'developer') || userHasRole('staff', 'marketing');

    // userHasRole doesn't seem to work because we need to fetch the active profile's roles.
    // Staff profile overwrites the member's roles. Use activeProfile rather than roles in local storage.
    let isOwner = false;
    if (getActiveProfile() !== null) {
        let roles = getActiveProfile().roles;
        isOwner = roles.includes('user.owner');
    }

    // Get accounting balance from the BillingBalances card to pass it down to the Deposits screen
    const refreshAccountBalance = useCallback((state) => {
        setAccountingBalance(state);
    }, []);

    // This hook acts as an onMount. If staff tries to access the submenu item before selecting a membership
    // redirect to select member page, before attempting to fire the API call which would throw an error.
    useEffect(() => {
        if (getAccountMembershipNumber() === null || getActiveAccount() === null) {
            navigate('/member_change');
        }
    }, [navigate]);

    // On mount fetch the taxes data.
    useEffect(() => {
        async function fetchTax() {
            setLoading(true);
            try {
                const signal = abortSignalRef.current;
                const data = await getTaxes(signal);

                if (signal.aborted === false) {
                    setTaxes(data);
                    setLoading(false);
                }
            } catch (err) {
                setLoading(false);
                showMessages(err.message, messageType.error);
            }
        }

        fetchTax();
    }, []);

    // Cleanup effect when component unmounts
    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    const tabsItems = [
        {
            label: 'Balances',
            key: 1,
            children:
                <ErrorBoundary>
                    <BillingBalancesStaff
                        refreshBalances={refreshBalances}
                        toggleRefreshBalances={setRefreshBalances}
                        refreshAccountBalance={refreshAccountBalance}
                        isOwner={isOwner}
                    />
                    <TransactionHistory
                        refreshBalances={refreshBalances}
                        toggleRefreshBalances={setRefreshBalances}
                    />
                </ErrorBoundary>
        },
        {
            label: 'Credits',
            key: 3,
            children:
                <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 16]}>
                        <Col xs={24} md={12} xl={9}>
                            <Card title="One-Time Credit">
                                <CreditCreateForm
                                    taxes={taxes}
                                    toggleRefreshBalances={setRefreshBalances}
                                />
                            </Card>
                        </Col>
                    </Row>
                </ErrorBoundary>
        },
        {
            label: 'Payments',
            key: 5,
            children:
                <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 16]}>
                        <Col xs={24} md={12} xl={9}>
                            <Card title="Create Payment Record">
                                <PaymentCreateForm
                                    paymentType="payment"
                                    toggleRefreshBalances={setRefreshBalances}
                                />
                            </Card>
                        </Col>
                        {
                            isModuleAvailable(MODULE_BILLING_CREDIT_CARD_PAYMENT) &&
                            <Col xs={24} md={12} xl={9}>
                                <Card title="Credit Card Payment">
                                    <ChargeCreditCardFormStaff
                                        toggleRefreshBalances={setRefreshBalances}
                                        refetchStoredCard={refetchStoredCard}
                                        setRefetchStoredCard={setRefetchStoredCard}
                                    />
                                </Card>
                            </Col>
                        }
                    </Row>
                </ErrorBoundary>
        },
        {
            label: 'Refunds',
            key: 7,
            children:
                <ErrorBoundary>
                    <Row gutter={[{xs: 0, md: 16}, 16]}>
                        <Col xs={24} md={12} xl={9}>
                            <Card title="One-Time Refund">
                                <PaymentCreateForm
                                    paymentType="refund"
                                    toggleRefreshBalances={setRefreshBalances}
                                />
                            </Card>
                        </Col>
                    </Row>
                </ErrorBoundary>
        },
    ];

    const chargesJSX =
        {
            label: 'Charges',
            key: 2,
            children:
                <ErrorBoundary>
                    <ChargeHistory
                        taxes={taxes}
                        toggleRefreshBalances={setRefreshBalances}
                    />
                </ErrorBoundary>
        };

    const depositsJSX =
        {
            label: 'Deposits',
            key: 4,
            children:
                <ErrorBoundary>
                    <Deposits accountingBalance={accountingBalance} />
                </ErrorBoundary>
        };

    const promoCreditsJSX =
        {
            label: 'Promotional Credits',
            key: 6,
            children:
                <ErrorBoundary>
                    <PromotionalCredits isOwner={isOwner}/>
                </ErrorBoundary>
        };

    const walletJSX =
        {
            label: 'Wallet',
            key: 8,
            children:
                <ErrorBoundary>
                    <StoredCreditCardCardStaff
                        toggleRefetchStoredCard={setRefetchStoredCard}
                    />
                </ErrorBoundary>
        };

    if (isMCT) {
        tabsItems.splice(1, 0, chargesJSX);
        tabsItems.splice(4, 0, promoCreditsJSX);
    }

    if (isMCT && isOwner) {
        tabsItems.splice(3, 0, depositsJSX);
    }

    if (isMarketing && !isMCT) {
        // Need to check !isMCT due to developer role, as staff.test will generate 2 promo credit tabs and break.
        tabsItems.splice(3, 0, promoCreditsJSX);
    }

    if (isModuleAvailable(MODULE_BILLING_CREDIT_CARD_PAYMENT)) {
        tabsItems.splice(7, 0, walletJSX);
    }

    return (
        loading ?
            <Skeleton/> :
            <>
                <PageTitle title="Billing" icon={<CreditCardOutlined/>}/>
                <Tabs defaultActiveKey="1" items={tabsItems} />
            </>
    )
}

export default BillingStaff;
